import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, Check, X, Eye, Users, UserCheck, MessageSquare, KeyRound, Shield, Plus, Trash2, Pencil, History, Undo2, PieChart, GraduationCap, BookOpen, Layers, AlertTriangle, UserMinus, Settings, Image, CalendarDays, Phone, Star, Megaphone, Bell, UserPlus, Mail, Database, Wrench, Link2Off, HelpCircle, Lightbulb, Clock, Upload, Download, ClipboardList, BadgeCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DemographicsAnalytics } from "@/components/admin/DemographicsAnalytics";
import { LearningAnalytics } from "@/components/admin/LearningAnalytics";

import { ErrorLogsSection } from "@/components/admin/ErrorLogsSection";
import { ProfileDetailDialog } from "@/components/directory/ProfileDetailDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { Textarea } from "@/components/ui/textarea";
import { ImagePicker } from "@/components/instructor/ImagePicker";
import { HomepageContentManager } from "@/components/admin/HomepageContentManager";
import GalleryManager from "@/components/admin/GalleryManager";
import { DualRoleRequestsPanel } from "@/components/admin/DualRoleRequestsPanel";
import { EventManager } from "@/components/admin/EventManager";
import { UpcomingCallsManager } from "@/components/admin/UpcomingCallsManager";
import { SatisfactionSurveyManager } from "@/components/admin/SatisfactionSurveyManager";
import PlatformSurveyManager from "@/components/admin/PlatformSurveyManager";
import { CommunityAnnouncementsManager } from "@/components/admin/CommunityAnnouncementsManager";
import { BulkEmailManager } from "@/components/admin/BulkEmailManager";
import { CurriculumReorderManager } from "@/components/admin/CurriculumReorderManager";
import { CurriculumVisibilityManager } from "@/components/admin/CurriculumVisibilityManager";
import { AskNedraInbox } from "@/components/admin/AskNedraInbox";
import { EmailTestingManager } from "@/components/admin/EmailTestingManager";
import { UserDeletionManager } from "@/components/admin/UserDeletionManager";
import { OrphanCleanupManager } from "@/components/admin/OrphanCleanupManager";
import { SecurityReportManager } from "@/components/admin/SecurityReportManager";
import { EmailTemplateManager } from "@/components/admin/EmailTemplateManager";
import { AdminSetPasswordDialog } from "@/components/admin/AdminSetPasswordDialog";
import { EmailMismatchAudit } from "@/components/admin/EmailMismatchAudit";
import { WelcomeEmailTemplatesManager } from "@/components/admin/WelcomeEmailTemplatesManager";
import { MentorTopicsManager } from "@/components/admin/MentorTopicsManager";
import { ConnectionResetManager } from "@/components/admin/ConnectionResetManager";
import { MentorshipTopicRequestsManager } from "@/components/admin/MentorshipTopicRequestsManager";
import { DirectMentorshipRequestsManager } from "@/components/admin/DirectMentorshipRequestsManager";
import { PendingProfilesManager } from "@/components/admin/PendingProfilesManager";
import { usePendingTopicRequestCount } from "@/hooks/useMentorshipTopicRequests";
import { usePendingDualRoleRequests } from "@/hooks/useDualRoleRequestsAdmin";
import { DiscussionModerationManager } from "@/components/admin/DiscussionModerationManager";
import { HelpCenterManager } from "@/components/admin/HelpCenterManager";
import { TaxonomySuggestionsManager } from "@/components/admin/TaxonomySuggestionsManager";
import { MentorshipMeetingsDiagnostics } from "@/components/admin/MentorshipMeetingsDiagnostics";
import { ZoomDebugLogs } from "@/components/admin/ZoomDebugLogs";
import { SchedulingDebugManager } from "@/components/admin/SchedulingDebugManager";
import { UserEmailVerificationManager } from "@/components/admin/UserEmailVerificationManager";
import { ConnectionsManager } from "@/components/admin/ConnectionsManager";
import { EmailNotificationsManager } from "@/components/admin/EmailNotificationsManager";
import AdminSuggestedConnections from "@/components/admin/AdminSuggestedConnections";
import { SendProfileNotificationDialog } from "@/components/admin/SendProfileNotificationDialog";
import { PhoneInput, parsePhoneNumber, combinePhoneNumber } from "@/components/ui/phone-input";
import { getStorageUrl } from "@/lib/storage";
import { BulkImport } from "@/components/admin/BulkImport";
import { exportAllUsersCSV } from "@/utils/exportAllUsersCSV";
import { BadgesManager } from "@/components/admin/BadgesManager";
import { useHasRole } from "@/hooks/useUserRoles";
import { WelcomeEmailJobsHistory } from "@/components/admin/WelcomeEmailJobsHistory";
import { HelpArticlesWidget } from "@/components/admin/dashboard/HelpArticlesWidget";
import { LearningAssignmentsWidget } from "@/components/admin/dashboard/LearningAssignmentsWidget";

const JAMAICAN_PARISHES = [
  { value: "kingston", label: "Kingston" },
  { value: "st_andrew", label: "St. Andrew" },
  { value: "st_thomas", label: "St. Thomas" },
  { value: "portland", label: "Portland" },
  { value: "st_mary", label: "St. Mary" },
  { value: "st_ann", label: "St. Ann" },
  { value: "trelawny", label: "Trelawny" },
  { value: "st_james", label: "St. James" },
  { value: "hanover", label: "Hanover" },
  { value: "westmoreland", label: "Westmoreland" },
  { value: "st_elizabeth", label: "St. Elizabeth" },
  { value: "manchester", label: "Manchester" },
  { value: "clarendon", label: "Clarendon" },
  { value: "st_catherine", label: "St. Catherine" },
];

const COUNTRY_OPTIONS = [
  { value: "jamaica", label: "Jamaica" },
  { value: "united_states", label: "United States" },
];

const PHONE_TYPES = [
  { value: "mobile", label: "Mobile" },
  { value: "home", label: "Home" },
  { value: "work", label: "Work" },
];

const REFERRED_BY_OPTIONS = [
  { value: "food_for_the_poor", label: "Food For The Poor" },
  { value: "emprezz_golding", label: "Emprezz Golding" },
  { value: "talk_up_yout", label: "Talk Up Yout" },
  { value: "word_of_mouth", label: "Word of Mouth" },
  { value: "social_media", label: "Social Media" },
  { value: "website", label: "Website" },
  { value: "other", label: "Other" },
];

const GENDER_OPTIONS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer Not to Say" },
];

const AGE_RANGE_OPTIONS = [
  { value: "12-17", label: "12-17" },
  { value: "18-30", label: "18-30" },
  { value: "31-45", label: "31-45" },
  { value: "45+", label: "45+" },
];

const HAS_DEVICE_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "maybe", label: "Maybe" },
];

const EDUCATION_LEVEL_OPTIONS = [
  { value: "some_high_school", label: "Some high school" },
  { value: "high_school_graduate", label: "High school graduate" },
  { value: "college", label: "College" },
  { value: "trade_school", label: "Trade School" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

// Top-level tab values rendered by the main <Tabs> strip below. Kept next to the
// URL parsing so a bad ?tab= falls back instead of rendering an empty page.
const VALID_ADMIN_TABS = [
  "profiles", "users", "demographics", "learning", "unenrollments", "gallery",
  "settings", "events", "calls", "satisfaction", "surveys", "communications",
  "ask-nedra", "topic-requests", "direct-mentorship", "recent-registrations",
  "email-testing", "admin-tools", "community-connections", "email-notifications",
  "suggested-connections", "bulk-import", "role-requests",
];

export default function Admin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: pendingDualRoleRequests } = usePendingDualRoleRequests();
  const queryClient = useQueryClient();
  
  // URL parameter handling for deep linking
  const urlTab = searchParams.get("tab");
  const urlThreadId = searchParams.get("threadId");
  // Validate ?tab= against the real top-level tab values. An unmatched value
  // rendered the tab strip with completely empty content, which is how
  // ?tab=import-history and ?tab=help-center produced blank pages.
  const [activeTab, setActiveTab] = useState(
    urlTab && VALID_ADMIN_TABS.includes(urlTab) ? urlTab : "profiles"
  );

  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showUserDetailDialog, setShowUserDetailDialog] = useState(false);
  const [viewingUserDetail, setViewingUserDetail] = useState<any>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState<Record<string, string>>({});
  const [resetingUserId, setResetingUserId] = useState<string | null>(null);
  const [setPasswordUser, setSetPasswordUser] = useState<{ id: string; email: string; full_name?: string | null } | null>(null);
  const [selectedProfiles, setSelectedProfiles] = useState<Set<string>>(new Set());
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  
  // Profile approval confirmation dialog state
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [profileToApprove, setProfileToApprove] = useState<{ id: string; full_name: string; role: string } | null>(null);
  const [bulkApprovalDialogOpen, setBulkApprovalDialogOpen] = useState(false);
  const [profilesToApprove, setProfilesToApprove] = useState<string[]>([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedProfileForEmail, setSelectedProfileForEmail] = useState<any>(null);
  // User management filters
  const [filterOnboarded, setFilterOnboarded] = useState<string>("all");
  const [filterUserType, setFilterUserType] = useState<string>("all");
  const [filterCurriculum, setFilterCurriculum] = useState<string>("all");
  const [filterCourse, setFilterCourse] = useState<string>("all");
  const [filterParish, setFilterParish] = useState<string>("all");
  const [filterProgram, setFilterProgram] = useState<string>("all");
  const { data: topShopUserIds } = useQuery({
    queryKey: ["admin", "top-shop-applicant-ids"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data: program } = await supabase.from("programs").select("id").eq("slug", "top-shop").maybeSingle();
      if (!program) return new Set<string>();
      const { data, error } = await supabase.from("program_applications").select("user_id").eq("program_id", program.id);
      if (error) throw error;
      return new Set<string>((data ?? []).map((r) => r.user_id as string));
    },
  });
  const [downloadingDeckUserId, setDownloadingDeckUserId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<{
    full_name: string;
    email: string;
    bio: string;
    role: "entrepreneur" | "mentor" | "admin";
    phone_country_code: string;
    phone_number: string;
    phone_type: string;
    parish: string;
    country: string;
    linkedin_url: string;
    gender: string;
    age_range: string;
    education_level: string;
    has_device: string;
    referred_by: string;
  }>({ 
    full_name: "", 
    email: "", 
    bio: "", 
    role: "entrepreneur",
    phone_country_code: "+1",
    phone_number: "",
    phone_type: "",
    parish: "",
    country: "",
    linkedin_url: "",
    gender: "",
    age_range: "",
    education_level: "",
    has_device: "",
    referred_by: "",
  });

  // Check if user is admin
  const hasAdminRole = useHasRole("admin");
  const { data: isAdmin, isLoading: adminLoading } = useQuery({
    queryKey: ["is_admin", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
      return data;
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!adminLoading && isAdmin === false) {
      toast.error("You don't have permission to access this page");
      navigate("/dashboard");
    }
  }, [isAdmin, adminLoading, navigate]);

  // Fetch pending profiles
  const { data: pendingProfiles, isLoading: pendingLoading } = useQuery({
    queryKey: ["pending_profiles"],
    queryFn: async () => {
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("is_published", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Fetch private data for pending profiles
      const profileIds = profiles?.map((p) => p.id) || [];
      let privateData: any[] = [];
      if (profileIds.length > 0) {
        const { data } = await supabase.from("profile_private").select("*").in("user_id", profileIds);
        privateData = data || [];
      }
      
      // Merge private data with profiles
      return profiles?.map((p) => {
        const privData = privateData.find((pd) => pd.user_id === p.id);
        return {
          ...p,
          email: privData?.email || null,
        };
      });
    },
    enabled: isAdmin === true,
  });


  // Fetch course unenrollments
  const { data: unenrollments, isLoading: unenrollmentsLoading } = useQuery({
    queryKey: ["admin_unenrollments"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_unenrollments")
        .select(`
          id,
          user_id,
          course_id,
          unenrolled_at,
          original_enrolled_at,
          progress_at_unenroll
        `)
        .order("unenrolled_at", { ascending: false });

      if (error) throw error;

      // Fetch user and course details
      const userIds = [...new Set(data?.map((u) => u.user_id) || [])];
      const courseIds = [...new Set(data?.map((u) => u.course_id) || [])];

      let profiles: any[] = [];
      let courses: any[] = [];

      if (userIds.length > 0) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url")
          .in("id", userIds);
        profiles = profileData || [];
      }

      if (courseIds.length > 0) {
        const { data: courseData } = await supabase
          .from("courses")
          .select("id, title")
          .in("id", courseIds);
        courses = courseData || [];
      }

      return data?.map((u) => ({
        ...u,
        user: profiles.find((p) => p.id === u.user_id),
        course: courses.find((c) => c.id === u.course_id),
      }));
    },
    enabled: isAdmin === true,
  });
  const { data: stats } = useQuery({
    queryKey: ["admin_stats"],
    queryFn: async () => {
      const { count: totalUsers } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true });

      const { count: publishedUsers } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("is_published", true);

      const { count: pendingUsers } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("is_published", false);

      const { count: totalConnections } = await supabase
        .from("connections")
        .select("*", { count: "exact", head: true });

      return {
        totalUsers: totalUsers || 0,
        publishedUsers: publishedUsers || 0,
        pendingUsers: pendingUsers || 0,
        totalConnections: totalConnections || 0,
      };
    },
    enabled: isAdmin === true,
  });

  // Fetch recent registrations (last 30 days)
  const { data: recentRegistrations, isLoading: recentRegistrationsLoading } = useQuery({
    queryKey: ["recent_registrations"],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("*")
        .gte("created_at", thirtyDaysAgo.toISOString())
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Fetch private data for recent registrations
      const profileIds = profiles?.map((p) => p.id) || [];
      let privateData: any[] = [];
      if (profileIds.length > 0) {
        const { data } = await supabase.from("profile_private").select("*").in("user_id", profileIds);
        privateData = data || [];
      }
      
      // Merge private data with profiles
      return profiles?.map((p) => {
        const privData = privateData.find((pd) => pd.user_id === p.id);
        return {
          ...p,
          email: privData?.email || null,
        };
      });
    },
    enabled: isAdmin === true,
  });


  // Fetch site settings
  const { data: heroBannerSetting } = useQuery({
    queryKey: ["site_setting_hero_banner"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .eq("key", "hero_banner_image")
        .single();
      
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: isAdmin === true,
  });

  // Fetch connection limit setting
  const { data: connectionLimitSetting } = useQuery({
    queryKey: ["site_setting_connection_limit"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .eq("key", "connection_limit_enabled")
        .single();
      
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: isAdmin === true,
  });

  const connectionLimitEnabled = connectionLimitSetting?.value === "true";

  // Fetch leaderboard setting
  const { data: leaderboardSetting } = useQuery({
    queryKey: ["site_setting_leaderboard"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .eq("key", "leaderboard_enabled")
        .single();
      
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: isAdmin === true,
  });

  const leaderboardEnabled = leaderboardSetting?.value === "true";

  // Fetch connection leaderboard setting
  const { data: connectionLeaderboardSetting } = useQuery({
    queryKey: ["site_setting_connection_leaderboard"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .eq("key", "connection_leaderboard_enabled")
        .single();
      
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: isAdmin === true,
  });

  const connectionLeaderboardEnabled = connectionLeaderboardSetting?.value === "true";

  // Fetch hide not started assignments setting
  const { data: hideNotStartedSetting } = useQuery({
    queryKey: ["site_setting_hide_not_started_assignments"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .eq("key", "hide_not_started_assignments")
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  const hideNotStartedEnabled = hideNotStartedSetting?.value === "true";

  // Toggle connection limit mutation
  const toggleConnectionLimit = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase
        .from("site_settings")
        .upsert({
          key: "connection_limit_enabled",
          value: enabled ? "true" : "false",
          updated_at: new Date().toISOString(),
          updated_by: user?.id,
        }, { onConflict: "key" });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site_setting_connection_limit"] });
      toast.success("Connection limit setting updated");
    },
    onError: (error) => {
      toast.error("Failed to update connection limit setting");
      console.error(error);
    },
  });

  // Toggle leaderboard mutation
  const toggleLeaderboard = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase
        .from("site_settings")
        .upsert({
          key: "leaderboard_enabled",
          value: enabled ? "true" : "false",
          updated_at: new Date().toISOString(),
          updated_by: user?.id,
        }, { onConflict: "key" });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site_setting_leaderboard"] });
      toast.success("Learning leaderboard setting updated");
    },
    onError: (error) => {
      toast.error("Failed to update leaderboard setting");
      console.error(error);
    },
  });

  // Toggle connection leaderboard mutation
  const toggleConnectionLeaderboard = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase
        .from("site_settings")
        .upsert({
          key: "connection_leaderboard_enabled",
          value: enabled ? "true" : "false",
          updated_at: new Date().toISOString(),
          updated_by: user?.id,
        }, { onConflict: "key" });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site_setting_connection_leaderboard"] });
      toast.success("Connection leaderboard setting updated");
    },
    onError: (error) => {
      toast.error("Failed to update connection leaderboard setting");
      console.error(error);
    },
  });

  // Toggle hide not started assignments mutation
  const toggleHideNotStarted = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase
        .from("site_settings")
        .upsert({
          key: "hide_not_started_assignments",
          value: enabled ? "true" : "false",
          updated_at: new Date().toISOString(),
          updated_by: user?.id,
        }, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site_setting_hide_not_started_assignments"] });
      toast.success("Not Started assignments visibility updated");
    },
    onError: (error) => {
      toast.error("Failed to update setting");
      console.error(error);
    },
  });

  // Update hero banner mutation
  const updateHeroBanner = useMutation({
    mutationFn: async (imageUrl: string | null) => {
      const { error } = await supabase
        .from("site_settings")
        .upsert({
          key: "hero_banner_image",
          value: imageUrl,
          updated_at: new Date().toISOString(),
          updated_by: user?.id,
        }, { onConflict: "key" });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site_setting_hero_banner"] });
      queryClient.invalidateQueries({ queryKey: ["site_setting_hero_banner_public"] });
      toast.success("Hero banner updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update hero banner");
      console.error(error);
    },
  });

  // Fetch all users for user management with enrollments and progress
  const { data: allUsers, isLoading: usersLoading } = useQuery({
    queryKey: ["all_users_full"],
    queryFn: async () => {
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select(`
          *,
          user_skills(skills(name)),
          user_industries(industries(name))
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch profile_private data for all users
      const profileIds = profiles?.map((p) => p.id) || [];
      let privateData: any[] = [];
      if (profileIds.length > 0) {
        const { data } = await supabase.from("profile_private").select("*").in("user_id", profileIds);
        privateData = data || [];
      }

      // Fetch role-specific profiles
      const mentorIds = profiles?.filter((p) => p.role === "mentor").map((p) => p.id) || [];
      const entrepreneurIds = profiles?.filter((p) => p.role === "entrepreneur").map((p) => p.id) || [];

      let mentorProfiles: any[] = [];
      let entrepreneurProfiles: any[] = [];

      if (mentorIds.length > 0) {
        const { data } = await supabase.from("mentor_profiles").select("*").in("id", mentorIds);
        mentorProfiles = data || [];
      }
      
      if (entrepreneurIds.length > 0) {
        const { data } = await supabase.from("entrepreneur_profiles").select("*").in("id", entrepreneurIds);
        entrepreneurProfiles = data || [];
      }

      // Create a map of private data by user_id
      const privateDataMap = new Map<string, any>();
      privateData.forEach((pd) => {
        privateDataMap.set(pd.user_id, pd);
      });

      // Fetch curriculum enrollments with curriculum details
      const { data: curriculumEnrollments } = await supabase
        .from("curriculum_enrollments")
        .select("user_id, curriculum_id, enrolled_at, curricula(id, title)");

      // Fetch course enrollments with course details
      const { data: courseEnrollments } = await supabase
        .from("course_enrollments")
        .select("id, user_id, course_id, enrolled_at, courses(id, title, curriculum_id)");

      // Fetch all modules for progress calculation
      const { data: allModules } = await supabase
        .from("modules")
        .select("id, course_id");

      // Fetch module progress
      const { data: moduleProgress } = await supabase
        .from("module_progress")
        .select("enrollment_id, module_id, completed_at");

      // Build lookup maps
      const curriculumEnrollmentsByUser = new Map<string, any[]>();
      curriculumEnrollments?.forEach((e) => {
        if (!curriculumEnrollmentsByUser.has(e.user_id)) {
          curriculumEnrollmentsByUser.set(e.user_id, []);
        }
        curriculumEnrollmentsByUser.get(e.user_id)!.push({
          curriculum_id: e.curriculum_id,
          title: (e.curricula as any)?.title || "Unknown",
          enrolled_at: e.enrolled_at,
        });
      });

      const courseEnrollmentsByUser = new Map<string, any[]>();
      courseEnrollments?.forEach((e) => {
        if (!courseEnrollmentsByUser.has(e.user_id)) {
          courseEnrollmentsByUser.set(e.user_id, []);
        }
        courseEnrollmentsByUser.get(e.user_id)!.push({
          enrollment_id: e.id,
          course_id: e.course_id,
          title: (e.courses as any)?.title || "Unknown",
          curriculum_id: (e.courses as any)?.curriculum_id,
          enrolled_at: e.enrolled_at,
        });
      });

      // Build module counts by course
      const modulesByCourse = new Map<string, string[]>();
      allModules?.forEach((m) => {
        if (!modulesByCourse.has(m.course_id)) {
          modulesByCourse.set(m.course_id, []);
        }
        modulesByCourse.get(m.course_id)!.push(m.id);
      });

      // Build completed modules by enrollment
      const completedByEnrollment = new Map<string, Set<string>>();
      moduleProgress?.forEach((mp) => {
        if (mp.completed_at) {
          if (!completedByEnrollment.has(mp.enrollment_id)) {
            completedByEnrollment.set(mp.enrollment_id, new Set());
          }
          completedByEnrollment.get(mp.enrollment_id)!.add(mp.module_id);
        }
      });

      // Calculate progress for each user's courses
      const userProgressData = new Map<string, {
        curricula: { id: string; title: string; progress: number; totalCourses: number; completedCourses: number; enrolled_at?: string }[];
        courses: { id: string; title: string; progress: number; totalModules: number; completedModules: number; curriculum_id?: string; enrolled_at?: string }[];
        totalModulesCompleted: number;
        totalModules: number;
      }>();

      profiles?.forEach((p) => {
        const userCurricula = curriculumEnrollmentsByUser.get(p.id) || [];
        const userCourses = courseEnrollmentsByUser.get(p.id) || [];
        
        let totalModulesCompleted = 0;
        let totalModules = 0;

        // Calculate course progress
        const coursesWithProgress = userCourses.map((c) => {
          const courseModules = modulesByCourse.get(c.course_id) || [];
          const completedModules = completedByEnrollment.get(c.enrollment_id) || new Set();
          const completedCount = courseModules.filter((m) => completedModules.has(m)).length;
          const totalCount = courseModules.length;
          
          totalModulesCompleted += completedCount;
          totalModules += totalCount;

          return {
            id: c.course_id,
            title: c.title,
            progress: totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0,
            totalModules: totalCount,
            completedModules: completedCount,
            curriculum_id: c.curriculum_id,
            enrolled_at: c.enrolled_at,
          };
        });

        // Calculate curriculum progress (based on courses within)
        const curriculaWithProgress = userCurricula.map((curr) => {
          const curriculumCourses = coursesWithProgress.filter((c) => c.curriculum_id === curr.curriculum_id);
          const completedCourses = curriculumCourses.filter((c) => c.progress === 100).length;
          const totalCourses = curriculumCourses.length;
          const avgProgress = totalCourses > 0 
            ? Math.round(curriculumCourses.reduce((sum, c) => sum + c.progress, 0) / totalCourses) 
            : 0;

          return {
            id: curr.curriculum_id,
            title: curr.title,
            progress: avgProgress,
            totalCourses,
            completedCourses,
            enrolled_at: curr.enrolled_at,
          };
        });

        userProgressData.set(p.id, {
          curricula: curriculaWithProgress,
          courses: coursesWithProgress,
          totalModulesCompleted,
          totalModules,
        });
      });

      return profiles?.map((p) => {
        const privData = privateDataMap.get(p.id);
        return {
          ...p,
          // Add private data fields
          email: privData?.email || null,
          phone: privData?.phone || null,
          phone_type: privData?.phone_type || null,
          linkedin_url: privData?.linkedin_url || null,
          parish: privData?.parish || null,
          country: privData?.country || null,
          skills: p.user_skills?.map((us: any) => us.skills) || [],
          industries: p.user_industries?.map((ui: any) => ui.industries) || [],
          mentor_profile: p.role === "mentor" ? mentorProfiles.find((rp) => rp.id === p.id) : null,
          entrepreneur_profile: p.role === "entrepreneur" ? entrepreneurProfiles.find((rp) => rp.id === p.id) : null,
          curriculum_ids: (curriculumEnrollmentsByUser.get(p.id) || []).map((c) => c.curriculum_id),
          course_ids: (courseEnrollmentsByUser.get(p.id) || []).map((c) => c.course_id),
          progressData: userProgressData.get(p.id) || { curricula: [], courses: [], totalModulesCompleted: 0, totalModules: 0 },
        };
      });
    },
    enabled: isAdmin === true,
  });

  // Fetch curricula for filter dropdown
  const { data: allCurricula } = useQuery({
    queryKey: ["all_curricula_admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("curricula")
        .select("id, title")
        .order("title");
      if (error) throw error;
      return data;
    },
    enabled: isAdmin === true,
  });

  // Fetch courses for filter dropdown
  const { data: allCourses } = useQuery({
    queryKey: ["all_courses_admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courses")
        .select("id, title")
        .order("title");
      if (error) throw error;
      return data;
    },
    enabled: isAdmin === true,
  });

  // Fetch user IDs that have pitch decks (for showing download button)
  const { data: pitchDeckUserIds } = useQuery({
    queryKey: ["admin_pitch_deck_user_ids"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pitch_decks")
        .select("user_id, file_key")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      // Keep only the most recent per user
      const map = new Map<string, string>();
      data?.forEach((d) => {
        if (!map.has(d.user_id)) map.set(d.user_id, d.file_key);
      });
      return map;
    },
    enabled: isAdmin === true,
  });

  const handleDownloadPitchDeck = async (userId: string) => {
    const fileKey = pitchDeckUserIds?.get(userId);
    if (!fileKey) return;
    setDownloadingDeckUserId(userId);
    try {
      const { data, error } = await supabase.storage
        .from("pitch-decks")
        .createSignedUrl(fileKey, 60);
      if (error || !data?.signedUrl) throw error || new Error("No URL returned");
      window.open(data.signedUrl, "_blank");
    } catch {
      toast.error("Unable to download pitch deck. Please try again.");
    } finally {
      setDownloadingDeckUserId(null);
    }
  };

  const uniqueParishes = Array.from(
    new Set(allUsers?.map((u) => u.parish).filter(Boolean) || [])
  ).sort() as string[];

  // Fetch all user roles
  const { data: allUserRoles } = useQuery({
    queryKey: ["all_user_roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("*");

      if (error) throw error;
      return data;
    },
    enabled: isAdmin === true,
  });

  // Fetch activity logs
  const { data: activityLogs, isLoading: logsLoading } = useQuery({
    queryKey: ["admin_activity_logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_activity_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      return data;
    },
    enabled: isAdmin === true,
  });

  // Helper function to log admin actions
  const logAdminAction = async (
    actionType: string,
    targetUserId?: string,
    targetUserEmail?: string,
    details?: Record<string, any>
  ) => {
    if (!user?.id) return;
    
    try {
      await supabase.from("admin_activity_logs").insert({
        admin_id: user.id,
        action_type: actionType,
        target_user_id: targetUserId,
        target_user_email: targetUserEmail,
        details: details || null,
      });
      queryClient.invalidateQueries({ queryKey: ["admin_activity_logs"] });
    } catch (error) {
      console.error("Failed to log admin action:", error);
    }
  };

  // Helper to get roles for a specific user
  const getUserRoles = (userId: string) => {
    return allUserRoles?.filter((r) => r.user_id === userId).map((r) => r.role) || [];
  };

  // Available app roles
  const availableRoles = ["admin", "moderator", "user", "instructor"] as const;

  // Add role mutation
  const addRole = useMutation({
    mutationFn: async ({ userId, role, userEmail }: { userId: string; role: string; userEmail?: string }) => {
      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: role as any });

      if (error) throw error;
      return { userId, role, userEmail };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all_user_roles"] });
      toast.success("Role added successfully!");
      logAdminAction("add_role", data.userId, data.userEmail, { role: data.role });
    },
    onError: (error: any) => {
      toast.error("Failed to add role: " + error.message);
    },
  });

  // Remove role mutation
  const removeRole = useMutation({
    mutationFn: async ({ userId, role, userEmail }: { userId: string; role: string; userEmail?: string }) => {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId)
        .eq("role", role as any);

      if (error) throw error;
      return { userId, role, userEmail };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all_user_roles"] });
      toast.success("Role removed successfully!");
      logAdminAction("remove_role", data.userId, data.userEmail, { role: data.role });
    },
    onError: (error: any) => {
      toast.error("Failed to remove role: " + error.message);
    },
  });

  // Update profile mutation — dual-write to profiles + profile_private + auth email
  const updateProfile = useMutation({
    mutationFn: async ({
      profileId,
      updates,
      userEmail,
      previousValues,
      originalEmail,
    }: {
      profileId: string;
      updates: Partial<typeof editFormData>;
      userEmail?: string;
      previousValues?: Partial<typeof editFormData>;
      originalEmail?: string | null;
    }) => {
      // Compose phone string from form parts
      const composedPhone = combinePhoneNumber(
        updates.phone_country_code ?? "+1",
        updates.phone_number ?? ""
      );

      // Payload 1 → profiles
      const profilesPayload: Record<string, any> = {
        full_name: updates.full_name,
        role: updates.role,
        bio: updates.bio,
        gender: updates.gender || null,
        age_range: updates.age_range || null,
        education_level: updates.education_level || null,
        has_device: updates.has_device || null,
        referred_by: updates.referred_by,
      };

      // Payload 2 → profile_private (contact + location fields)
      // NOTE: email is intentionally NOT written here when it changed —
      // the admin-update-user-email edge function is the single source of
      // truth for email changes and updates both auth.users and
      // profile_private in one flow. Writing email here in parallel would
      // race with that function and could leave the two out of sync.
      const newEmail = (updates.email ?? "").trim().toLowerCase();
      const prevEmail = (originalEmail ?? "").trim().toLowerCase();
      const emailChanged = !!newEmail && newEmail !== prevEmail;

      const privatePayload: Record<string, any> = {
        country: updates.country || null,
        linkedin_url: updates.linkedin_url,
        parish: updates.parish || null,
        phone: composedPhone,
        phone_type: updates.phone_type || null,
      };
      if (!emailChanged) {
        // Safe to include email — it isn't actually changing, but we still
        // sync any casing/whitespace fixes.
        privatePayload.email = updates.email;
      }

      // Step 1: if the email is changing, update auth.users FIRST via the
      // admin edge function. If that fails, abort before touching profiles/
      // profile_private so the two sides can't drift apart.
      if (emailChanged) {
        const authRes = await supabase.functions.invoke("admin-update-user-email", {
          body: { userId: profileId, newEmail },
        });
        let fnErr: string | null = null;
        const rawErr = (authRes as any)?.error;
        if (rawErr) {
          try {
            const resp = rawErr?.context?.response;
            if (resp && typeof resp.json === "function") {
              const parsed = await resp.clone().json();
              fnErr = parsed?.error || JSON.stringify(parsed);
            } else if (resp && typeof resp.text === "function") {
              fnErr = await resp.clone().text();
            }
          } catch {
            // fall through
          }
          if (!fnErr) fnErr = rawErr?.message || "Unknown edge function error";
        } else if ((authRes as any)?.data?.error) {
          fnErr = (authRes as any).data.error;
        } else if ((authRes as any)?.data && (authRes as any).data.in_sync === false) {
          fnErr = "Auth and profile emails do not match after save.";
        }
        if (fnErr) {
          throw new Error(`auth email: ${fnErr}`);
        }
      }

      // Step 2: write profiles + profile_private (email already handled above)
      const [profilesRes, privateRes] = await Promise.all([
        supabase.from("profiles").update(profilesPayload).eq("id", profileId),
        supabase.from("profile_private").update(privatePayload).eq("user_id", profileId),
      ]);

      const errors: string[] = [];
      if (profilesRes.error) errors.push(`profiles: ${profilesRes.error.message}`);
      if (privateRes.error) errors.push(`profile_private: ${privateRes.error.message}`);
      if (errors.length > 0) {
        throw new Error(errors.join("; "));
      }

      // Step 3: safeguard — re-verify sign-in email matches profile email.
      if (emailChanged) {
        const { data: checkRow } = await supabase
          .from("profile_private")
          .select("email")
          .eq("user_id", profileId)
          .maybeSingle();
        const finalProfileEmail = (checkRow?.email ?? "").trim().toLowerCase();
        if (finalProfileEmail !== newEmail) {
          throw new Error(
            `Verification failed: profile email is "${finalProfileEmail}" but expected "${newEmail}". Sign-in and profile emails may have drifted.`,
          );
        }
      }

      return { profileId, updates, userEmail, previousValues };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all_users"] });
      queryClient.invalidateQueries({ queryKey: ["profile_private"] });
      toast.success("Profile updated successfully!");
      setShowEditDialog(false);
      setSelectedProfile(null);
      logAdminAction("edit_profile", data.profileId, data.userEmail, {
        changes: data.updates,
        previous_values: data.previousValues
      });
    },
    onError: (error: any) => {
      toast.error("Failed to update profile: " + error.message);
    },
  });

  // Delete user mutation (deletes from profiles table - cascade will handle auth)
  const deleteUser = useMutation({
    mutationFn: async ({ profileId, userEmail }: { profileId: string; userEmail?: string }) => {
      // First delete from profiles (which cascades to related tables)
      const { error } = await supabase
        .from("profiles")
        .delete()
        .eq("id", profileId);

      if (error) throw error;
      return { profileId, userEmail };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all_users"] });
      queryClient.invalidateQueries({ queryKey: ["admin_stats"] });
      queryClient.invalidateQueries({ queryKey: ["all_user_roles"] });
      toast.success("User deleted successfully!");
      setShowDeleteDialog(false);
      setSelectedProfile(null);
      logAdminAction("delete_user", data.profileId, data.userEmail);
    },
    onError: (error: any) => {
      toast.error("Failed to delete user: " + error.message);
    },
  });

  // Bulk delete mutation
  const bulkDeleteUsers = useMutation({
    mutationFn: async ({ userIds, count }: { userIds: string[]; count: number }) => {
      const { error } = await supabase
        .from("profiles")
        .delete()
        .in("id", userIds);

      if (error) throw error;
      return { userIds, count };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all_users"] });
      queryClient.invalidateQueries({ queryKey: ["admin_stats"] });
      queryClient.invalidateQueries({ queryKey: ["all_user_roles"] });
      setSelectedUsers(new Set());
      setShowBulkDeleteDialog(false);
      toast.success(`${data.count} users deleted successfully!`);
      logAdminAction("bulk_delete_users", undefined, undefined, { count: data.count, user_ids: data.userIds });
    },
    onError: (error: any) => {
      toast.error("Failed to delete users: " + error.message);
    },
  });

  // Bulk password reset mutation
  const bulkResetPasswords = useMutation({
    mutationFn: async (userEmails: string[]) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const results = await Promise.allSettled(
        userEmails.map((email) =>
          supabase.functions.invoke("admin-reset-password", {
            body: { user_email: email },
          })
        )
      );

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        throw new Error(`${failed.length} of ${userEmails.length} password resets failed`);
      }

      return { emails: userEmails, count: userEmails.length };
    },
    onSuccess: (data) => {
      setSelectedUsers(new Set());
      toast.success(`Password reset emails sent to ${data.count} users!`);
      logAdminAction("bulk_password_reset", undefined, undefined, { count: data.count, emails: data.emails });
    },
    onError: (error: any) => {
      toast.error("Bulk password reset failed: " + error.message);
    },
  });

  // Bulk add admin role mutation
  const bulkAddAdminRole = useMutation({
    mutationFn: async (userIds: string[]) => {
      // Insert admin role for each user (ignore if already exists)
      const inserts = userIds.map((userId) => ({
        user_id: userId,
        role: "admin" as const,
      }));

      const { error } = await supabase
        .from("user_roles")
        .upsert(inserts, { onConflict: "user_id,role", ignoreDuplicates: true });

      if (error) throw error;
      return { userIds, count: userIds.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all_user_roles"] });
      setSelectedUsers(new Set());
      toast.success(`Admin role added to ${data.count} users!`);
      logAdminAction("bulk_add_admin_role", undefined, undefined, { count: data.count, user_ids: data.userIds });
    },
    onError: (error: any) => {
      toast.error("Failed to add admin roles: " + error.message);
    },
  });

  // Undo add role - removes the role that was added
  const undoAddRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId)
        .eq("role", role as any);
      if (error) throw error;
      return { userId, role };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all_user_roles"] });
      queryClient.invalidateQueries({ queryKey: ["admin_activity_logs"] });
      toast.success("Role addition undone!");
      logAdminAction("undo_add_role", data.userId, undefined, { role: data.role });
    },
    onError: (error: any) => {
      toast.error("Failed to undo: " + error.message);
    },
  });

  // Undo remove role - re-adds the role that was removed
  const undoRemoveRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: role as any });
      if (error) throw error;
      return { userId, role };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all_user_roles"] });
      queryClient.invalidateQueries({ queryKey: ["admin_activity_logs"] });
      toast.success("Role removal undone!");
      logAdminAction("undo_remove_role", data.userId, undefined, { role: data.role });
    },
    onError: (error: any) => {
      toast.error("Failed to undo: " + error.message);
    },
  });

  // Undo profile edit - restores previous values
  const undoEditProfile = useMutation({
    mutationFn: async ({ profileId, previousValues }: { profileId: string; previousValues: Record<string, unknown> }) => {
      const { error } = await supabase
        .from("profiles")
        .update(previousValues)
        .eq("id", profileId);
      if (error) throw error;
      return { profileId, previousValues };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all_users"] });
      queryClient.invalidateQueries({ queryKey: ["admin_activity_logs"] });
      toast.success("Profile edit undone!");
      logAdminAction("undo_edit_profile", data.profileId, undefined, { restored: data.previousValues });
    },
    onError: (error: any) => {
      toast.error("Failed to undo: " + error.message);
    },
  });

  // Undo bulk add admin role - removes admin role from all affected users
  const undoBulkAddAdminRole = useMutation({
    mutationFn: async (userIds: string[]) => {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .in("user_id", userIds)
        .eq("role", "admin");
      if (error) throw error;
      return { userIds, count: userIds.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all_user_roles"] });
      queryClient.invalidateQueries({ queryKey: ["admin_activity_logs"] });
      toast.success(`Admin role removed from ${data.count} users!`);
      logAdminAction("undo_bulk_add_admin_role", undefined, undefined, { count: data.count });
    },
    onError: (error: any) => {
      toast.error("Failed to undo: " + error.message);
    },
  });

  // Helper to check if an action can be undone
  const canUndoAction = (log: any): boolean => {
    const details = log.details as Record<string, unknown> | null;
    
    switch (log.action_type) {
      case "add_role":
      case "remove_role":
        return !!(log.target_user_id && details?.role);
      case "edit_profile":
        return !!(log.target_user_id && details?.previous_values);
      case "bulk_add_admin_role":
        return !!(details?.user_ids && Array.isArray(details.user_ids));
      default:
        return false;
    }
  };

  // Handle undo action
  const handleUndo = (log: any) => {
    const details = log.details as Record<string, unknown> | null;
    
    switch (log.action_type) {
      case "add_role":
        if (log.target_user_id && details?.role) {
          undoAddRole.mutate({ userId: log.target_user_id, role: details.role as string });
        }
        break;
      case "remove_role":
        if (log.target_user_id && details?.role) {
          undoRemoveRole.mutate({ userId: log.target_user_id, role: details.role as string });
        }
        break;
      case "edit_profile":
        if (log.target_user_id && details?.previous_values) {
          undoEditProfile.mutate({ 
            profileId: log.target_user_id, 
            previousValues: details.previous_values as Record<string, unknown> 
          });
        } else {
          toast.error("Cannot undo - original values not available");
        }
        break;
      case "bulk_add_admin_role":
        if (details?.user_ids && Array.isArray(details.user_ids)) {
          undoBulkAddAdminRole.mutate(details.user_ids as string[]);
        }
        break;
      default:
        toast.error("This action cannot be undone");
    }
  };

  // Filter users based on search and filters
  const filteredUsers = allUsers?.filter((u) => {
    // Text search
    const matchesSearch =
      u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase());
    if (!matchesSearch) return false;

    // Onboarded filter
    if (filterOnboarded === "yes" && !u.is_onboarded) return false;
    if (filterOnboarded === "no" && u.is_onboarded) return false;

    // User type filter
    if (filterUserType !== "all" && u.role !== filterUserType) return false;

    // Parish filter
    if (filterParish !== "all" && u.parish !== filterParish) return false;

    // Curriculum filter
    if (filterCurriculum !== "all") {
      const userCurriculumIds = (u as any).curriculum_ids || [];
      if (!userCurriculumIds.includes(filterCurriculum)) return false;
    }

    // Course filter
    if (filterCourse !== "all") {
      const userCourseIds = (u as any).course_ids || [];
      if (!userCourseIds.includes(filterCourse)) return false;
    }

    // Program filter
    if (filterProgram === "top-shop" && !topShopUserIds?.has(u.id)) return false;

    return true;
  });

  // Admin password reset mutation
  const resetPassword = useMutation({
    mutationFn: async (userEmail: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await supabase.functions.invoke("admin-reset-password", {
        body: { user_email: userEmail },
      });

      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || "Password reset email sent!");
      setResetingUserId(null);
    },
    onError: (error: any) => {
      toast.error("Failed to send reset email: " + error.message);
      setResetingUserId(null);
    },
  });

  const publishProfile = useMutation({
    mutationFn: async (profileId: string) => {
      const { error } = await supabase
        .from("profiles")
        .update({
          is_published: true,
          is_verified: true,
          admin_verified_at: new Date().toISOString(),
          admin_verified_by: user?.id,
        })
        .eq("id", profileId);

      if (error) throw error;

      // Send welcome email using the unified edge function
      try {
        await supabase.functions.invoke("admin-send-welcome-approved", {
          body: { profileId },
        });
      } catch (emailError) {
        console.error("Failed to send welcome email:", emailError);
        // Don't fail the publish if email fails
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending_profiles"] });
      queryClient.invalidateQueries({ queryKey: ["admin_stats"] });
      toast.success("Profile published successfully!");
      setShowProfileDialog(false);
    },
    onError: (error) => {
      toast.error("Failed to publish profile: " + error.message);
    },
  });

  const unpublishProfile = useMutation({
    mutationFn: async (profileId: string) => {
      const { error } = await supabase
        .from("profiles")
        .update({
          is_published: false,
          is_verified: false,
        })
        .eq("id", profileId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending_profiles"] });
      queryClient.invalidateQueries({ queryKey: ["admin_stats"] });
      queryClient.invalidateQueries({ queryKey: ["all_users"] });
      toast.success("Profile unpublished successfully!");
      setShowProfileDialog(false);
    },
    onError: (error) => {
      toast.error("Failed to unpublish profile: " + error.message);
    },
  });

  const toggleOnboarded = useMutation({
    mutationFn: async ({ profileId, isOnboarded }: { profileId: string; isOnboarded: boolean }) => {
      const { error } = await supabase
        .from("profiles")
        .update({ is_onboarded: isOnboarded })
        .eq("id", profileId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all_users_full"] });
      toast.success("Onboarded status updated!");
    },
    onError: (error) => {
      toast.error("Failed to update onboarded status: " + error.message);
    },
  });

  const bulkPublishProfiles = useMutation({
    mutationFn: async (profileIds: string[]) => {
      const { error } = await supabase
        .from("profiles")
        .update({
          is_published: true,
          is_verified: true,
          admin_verified_at: new Date().toISOString(),
          admin_verified_by: user?.id,
        })
        .in("id", profileIds);

      if (error) throw error;

      // Send welcome emails for each profile using the unified edge function
      for (const profileId of profileIds) {
        try {
          await supabase.functions.invoke("admin-send-welcome-approved", {
            body: { profileId },
          });
        } catch (emailError) {
          console.error("Failed to send welcome email for profile:", profileId, emailError);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending_profiles"] });
      queryClient.invalidateQueries({ queryKey: ["admin_stats"] });
      setSelectedProfiles(new Set());
      toast.success(`${selectedProfiles.size} profiles published successfully!`);
    },
    onError: (error) => {
      toast.error("Failed to bulk publish: " + error.message);
    },
  });


  // Mutation to initiate or find existing connection for admin messaging
  const initiateConnection = useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!user?.id) throw new Error("Not authenticated");

      // Check for existing connection where admin is involved in the unified connections table
      const { data: existingConnection, error: findError } = await supabase
        .from("connections")
        .select("id")
        .or(`and(requester_id.eq.${user.id},recipient_id.eq.${targetUserId}),and(requester_id.eq.${targetUserId},recipient_id.eq.${user.id})`)
        .eq("status", "accepted")
        .maybeSingle();

      if (findError) throw findError;

      if (existingConnection) {
        return { connectionId: existingConnection.id, isNew: false };
      }

      // Create new connection with admin as recipient (admin reaching out)
      const { data: newConnection, error: createError } = await supabase
        .from("connections")
        .insert({
          requester_id: targetUserId,
          recipient_id: user.id,
          status: "accepted",
          connection_type: "mentorship",
          message: "Admin initiated contact",
          admin_approved_at: new Date().toISOString(),
          admin_approved_by: user.id,
          accepted_at: new Date().toISOString(),
        })
        .select("id")
        .single();

      if (createError) throw createError;

      return { connectionId: newConnection.id, isNew: true };
    },
    onSuccess: () => {
      navigate("/connections");
    },
    onError: (error: any) => {
      toast.error("Failed to initiate messaging: " + error.message);
    },
  });

  const handleMessageUser = (targetUserId: string, userName?: string) => {
    if (!targetUserId) {
      toast.error("User not found");
      return;
    }
    toast.info(`Opening conversation with ${userName || "user"}...`);
    initiateConnection.mutate(targetUserId);
  };

  return (
    <DashboardLayout>
      {(authLoading || profileLoading || adminLoading) ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (!user || !profile || !isAdmin) ? null : (
      <>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage users and connection requests</p>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalUsers || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Published</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.publishedUsers || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Approval</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.pendingUsers || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Connections</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalConnections || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Content Widgets */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <HelpArticlesWidget />
          <LearningAssignmentsWidget />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex-wrap h-auto gap-1">
            <TabsTrigger value="profiles">
              Pending Profiles {pendingProfiles && pendingProfiles.length > 0 && `(${pendingProfiles.length})`}
            </TabsTrigger>
            <TabsTrigger value="users">
              User Management
            </TabsTrigger>
            <TabsTrigger value="demographics">
              <PieChart className="h-4 w-4 mr-1" />
              Demographics
            </TabsTrigger>
            <TabsTrigger value="learning">
              <GraduationCap className="h-4 w-4 mr-1" />
              Learning
            </TabsTrigger>
            <TabsTrigger value="unenrollments">
              <UserMinus className="h-4 w-4 mr-1" />
              Unenrollments {unenrollments && unenrollments.length > 0 && `(${unenrollments.length})`}
            </TabsTrigger>
            <TabsTrigger value="gallery">
              <Image className="h-4 w-4 mr-1" />
              Gallery
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-1" />
              Site Settings
            </TabsTrigger>
            <TabsTrigger value="events">
              <CalendarDays className="h-4 w-4 mr-1" />
              Events
            </TabsTrigger>
            <TabsTrigger value="calls">
              <Phone className="h-4 w-4 mr-1" />
              Upcoming Calls
            </TabsTrigger>
            <TabsTrigger value="satisfaction">
              <Star className="h-4 w-4 mr-1" />
              Satisfaction Surveys
            </TabsTrigger>
            <TabsTrigger value="surveys">
              <ClipboardList className="h-4 w-4 mr-1" />
              Surveys
            </TabsTrigger>
            <TabsTrigger value="communications">
              <Megaphone className="h-4 w-4 mr-1" />
              Communications
            </TabsTrigger>
            <TabsTrigger value="ask-nedra">
              <MessageSquare className="h-4 w-4 mr-1" />
              Ask Nedra
            </TabsTrigger>
            <TabsTrigger value="topic-requests">
              <Lightbulb className="h-4 w-4 mr-1" />
              Topic Requests
            </TabsTrigger>
            <TabsTrigger value="direct-mentorship">
              <MessageSquare className="h-4 w-4 mr-1" />
              Direct Mentorship
            </TabsTrigger>
            <TabsTrigger value="recent-registrations">
              <UserPlus className="h-4 w-4 mr-1" />
              Recent Registrations {recentRegistrations && recentRegistrations.length > 0 && `(${recentRegistrations.length})`}
            </TabsTrigger>
            <TabsTrigger value="email-testing">
              <Mail className="h-4 w-4 mr-1" />
              Email Testing
            </TabsTrigger>
            <TabsTrigger value="admin-tools">
              <Wrench className="h-4 w-4 mr-1" />
              Admin Tools
            </TabsTrigger>
            <TabsTrigger value="community-connections">
              <Link2Off className="h-4 w-4 mr-1" />
              Community Connections
            </TabsTrigger>
            <TabsTrigger value="email-notifications">
              <Bell className="h-4 w-4 mr-1" />
              Email Notifications
            </TabsTrigger>
            <TabsTrigger value="suggested-connections">
              <UserPlus className="h-4 w-4 mr-1" />
              Suggested Connections
            </TabsTrigger>
            <TabsTrigger value="bulk-import">
              <Upload className="h-4 w-4 mr-1" />
              Bulk Import
            </TabsTrigger>
            <TabsTrigger value="role-requests">
              <UserPlus className="h-4 w-4 mr-1" />
              Role Requests
              {(pendingDualRoleRequests?.length ?? 0) > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs">
                  {pendingDualRoleRequests!.length}
                </Badge>
              )}
            </TabsTrigger>
            {hasAdminRole && (
              <TabsTrigger value="badges">
                <BadgeCheck className="h-4 w-4 mr-1" />
                Badges
              </TabsTrigger>
            )}
          </TabsList>

          {hasAdminRole && (
            <TabsContent value="badges" className="mt-6">
              <BadgesManager />
            </TabsContent>
          )}

          <TabsContent value="role-requests" className="mt-6">
            <DualRoleRequestsPanel />
          </TabsContent>

          <TabsContent value="profiles" className="mt-6">
            <PendingProfilesManager />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>Manage user profiles, roles, and perform bulk actions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  {/* Search and Filters */}
                  <div className="flex flex-col gap-4">
                    <Input
                      placeholder="Search by name or email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="max-w-md"
                    />
                    <div className="flex flex-wrap gap-3">
                      <Select value={filterOnboarded} onValueChange={setFilterOnboarded}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Onboarded" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Onboarding</SelectItem>
                          <SelectItem value="yes">Onboarded</SelectItem>
                          <SelectItem value="no">Not Onboarded</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={filterUserType} onValueChange={setFilterUserType}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="User Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
                          <SelectItem value="mentor">Mentor</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={filterCurriculum} onValueChange={setFilterCurriculum}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Curriculum" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Curricula</SelectItem>
                          {allCurricula?.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select value={filterCourse} onValueChange={setFilterCourse}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Course" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Courses</SelectItem>
                          {allCourses?.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select value={filterParish} onValueChange={setFilterParish}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Parish" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Parishes</SelectItem>
                          {uniqueParishes.map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select value={filterProgram} onValueChange={setFilterProgram}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Program" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All programs</SelectItem>
                          <SelectItem value="top-shop">Top Shop applicants</SelectItem>
                        </SelectContent>
                      </Select>

                      {(filterOnboarded !== "all" || filterUserType !== "all" || filterCurriculum !== "all" || filterCourse !== "all" || filterParish !== "all" || filterProgram !== "all") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setFilterOnboarded("all");
                            setFilterUserType("all");
                            setFilterCurriculum("all");
                            setFilterCourse("all");
                            setFilterParish("all");
                            setFilterProgram("all");
                          }}
                        >
                          Clear Filters
                        </Button>
                      )}
                    </div>
                    {filteredUsers && (
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-sm text-muted-foreground">
                          Showing {filteredUsers.length} of {allUsers?.length || 0} users
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => exportAllUsersCSV(filteredUsers)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Export Filtered ({filteredUsers.length})
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => exportAllUsersCSV(allUsers)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Export All Registrants ({allUsers?.length || 0})
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bulk Actions Bar */}
                {filteredUsers && filteredUsers.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
                          } else {
                            setSelectedUsers(new Set());
                          }
                        }}
                      />
                      <span className="text-sm text-muted-foreground">
                        {selectedUsers.size > 0 ? `${selectedUsers.size} selected` : "Select all"}
                      </span>
                    </div>
                    {selectedUsers.size > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const emails = filteredUsers
                              .filter(u => selectedUsers.has(u.id))
                              .map(u => u.email);
                            bulkResetPasswords.mutate(emails);
                          }}
                          disabled={bulkResetPasswords.isPending}
                        >
                          {bulkResetPasswords.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          <KeyRound className="h-4 w-4 mr-1" />
                          Reset Passwords ({selectedUsers.size})
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => bulkAddAdminRole.mutate(Array.from(selectedUsers))}
                          disabled={bulkAddAdminRole.isPending}
                        >
                          {bulkAddAdminRole.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          <Shield className="h-4 w-4 mr-1" />
                          Add Admin Role ({selectedUsers.size})
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setShowBulkDeleteDialog(true)}
                          disabled={bulkDeleteUsers.isPending}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete ({selectedUsers.size})
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                
                {usersLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : filteredUsers && filteredUsers.length > 0 ? (
                  <div className="space-y-4">
                    {filteredUsers.map((u) => {
                      const userRoles = getUserRoles(u.id);
                      const rolesNotAssigned = availableRoles.filter((r) => !userRoles.includes(r));
                      
                      return (
                        <div
                          key={u.id}
                          className="p-3 md:p-4 rounded-lg border bg-card space-y-3"
                        >
                          {/* Mobile layout */}
                          <div className="flex flex-col gap-3 md:hidden">
                            {/* User info row */}
                            <div className="flex items-start gap-3">
                              <Checkbox
                                className="mt-1"
                                checked={selectedUsers.has(u.id)}
                                onCheckedChange={(checked) => {
                                  const newSelected = new Set(selectedUsers);
                                  if (checked) {
                                    newSelected.add(u.id);
                                  } else {
                                    newSelected.delete(u.id);
                                  }
                                  setSelectedUsers(newSelected);
                                }}
                              />
                              <Avatar className="h-10 w-10 flex-shrink-0">
                                <AvatarImage src={getStorageUrl("avatars", u.avatar_url) || undefined} />
                                <AvatarFallback className="bg-primary text-primary-foreground">
                                  {u.full_name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{u.full_name}{topShopUserIds?.has(u.id) && <Badge variant="outline" className="ml-2 text-[10px]">Top Shop</Badge>}</p>
                                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                                {u.created_at && (
                                  <p className="text-xs text-muted-foreground">
                                    Registered: {format(new Date(u.created_at), "MMM d, yyyy")}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Badges row */}
                            <div className="flex flex-wrap gap-1.5 pl-[52px]">
                              <Badge 
                                variant={u.role === "mentor" ? "default" : "secondary"}
                                className="capitalize text-xs"
                              >
                                {u.role}
                              </Badge>
                              {u.is_published ? (
                                <Badge className="bg-green-600 text-white text-xs">Published</Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground text-xs">Unpublished</Badge>
                              )}
                              {u.is_onboarded && (
                                <Badge className="bg-blue-600 text-white text-xs">Onboarded</Badge>
                              )}
                            </div>
                            
                            {/* Action buttons - wrapped grid */}
                            <div className="grid grid-cols-2 gap-2 pl-[52px]">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-9"
                                onClick={() => {
                                  setViewingUserDetail(u);
                                  setShowUserDetailDialog(true);
                                }}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-9"
                                onClick={() => {
                                  setSelectedProfile(u);
                                  const { countryCode, phoneNumber } = parsePhoneNumber(u.phone || "");
                                  setEditFormData({
                                    full_name: u.full_name || "",
                                    email: u.email || "",
                                    bio: u.bio || "",
                                    role: u.role as "entrepreneur" | "mentor" | "admin",
                                    phone_country_code: countryCode,
                                    phone_number: phoneNumber,
                                    phone_type: u.phone_type || "",
                                    parish: u.parish || "",
                                    country: (u as any).country || "",
                                    linkedin_url: u.linkedin_url || "",
                                    gender: u.gender || "",
                                    age_range: u.age_range || "",
                                    education_level: u.education_level || "",
                                    has_device: u.has_device || "",
                                    referred_by: u.referred_by || "",
                                  });
                                  setShowEditDialog(true);
                                }}
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              {u.is_published ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-9 text-destructive border-destructive hover:bg-destructive/10"
                                  onClick={() => unpublishProfile.mutate(u.id)}
                                  disabled={unpublishProfile.isPending}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Unpublish
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  className="h-9 bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => {
                                    setProfileToApprove({ id: u.id, full_name: u.full_name || '', role: u.role || 'entrepreneur' });
                                    setApprovalDialogOpen(true);
                                  }}
                                  disabled={publishProfile.isPending}
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Publish
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-9"
                                onClick={() => {
                                  setResetingUserId(u.id);
                                  resetPassword.mutate(u.email);
                                }}
                                disabled={resetPassword.isPending && resetingUserId === u.id}
                              >
                                <KeyRound className="h-4 w-4 mr-1" />
                                Reset
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-9"
                                onClick={() =>
                                  setSetPasswordUser({ id: u.id, email: u.email, full_name: u.full_name })
                                }
                              >
                                <KeyRound className="h-4 w-4 mr-1" />
                                Set PW
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="h-9 col-span-2"
                                onClick={() => {
                                  setSelectedProfile(u);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete User
                              </Button>
                              {u.role?.toLowerCase() === "entrepreneur" && pitchDeckUserIds?.has(u.id) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-9 col-span-2"
                                  onClick={() => handleDownloadPitchDeck(u.id)}
                                  disabled={downloadingDeckUserId === u.id}
                                >
                                  {downloadingDeckUserId === u.id ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4 mr-1" />
                                  )}
                                  Pitch Deck
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Desktop layout */}
                          <div className="hidden md:flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={selectedUsers.has(u.id)}
                                onCheckedChange={(checked) => {
                                  const newSelected = new Set(selectedUsers);
                                  if (checked) {
                                    newSelected.add(u.id);
                                  } else {
                                    newSelected.delete(u.id);
                                  }
                                  setSelectedUsers(newSelected);
                                }}
                              />
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={getStorageUrl("avatars", u.avatar_url) || undefined} />
                                <AvatarFallback className="bg-primary text-primary-foreground">
                                  {u.full_name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{u.full_name}{topShopUserIds?.has(u.id) && <Badge variant="outline" className="ml-2 text-[10px]">Top Shop</Badge>}</p>
                                <p className="text-sm text-muted-foreground">{u.email}</p>
                                {u.created_at && (
                                  <p className="text-xs text-muted-foreground">
                                    Registered: {format(new Date(u.created_at), "MMM d, yyyy")}
                                  </p>
                                )}
                              </div>
                              <Badge 
                                variant={u.role === "mentor" ? "default" : "secondary"}
                                className="ml-2 capitalize"
                              >
                                {u.role}
                              </Badge>
                              {u.is_published ? (
                                <Badge className="bg-green-600 text-white">Published</Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground">Unpublished</Badge>
                              )}
                              {u.is_onboarded && (
                                <Badge className="bg-blue-600 text-white">Onboarded</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setViewingUserDetail(u);
                                  setShowUserDetailDialog(true);
                                }}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedProfile(u);
                                  const { countryCode, phoneNumber } = parsePhoneNumber(u.phone || "");
                                  setEditFormData({
                                    full_name: u.full_name || "",
                                    email: u.email || "",
                                    bio: u.bio || "",
                                    role: u.role as "entrepreneur" | "mentor" | "admin",
                                    phone_country_code: countryCode,
                                    phone_number: phoneNumber,
                                    phone_type: u.phone_type || "",
                                    parish: u.parish || "",
                                    country: (u as any).country || "",
                                    linkedin_url: u.linkedin_url || "",
                                    gender: u.gender || "",
                                    age_range: u.age_range || "",
                                    education_level: u.education_level || "",
                                    has_device: u.has_device || "",
                                    referred_by: u.referred_by || "",
                                  });
                                  setShowEditDialog(true);
                                }}
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              {u.is_published ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => unpublishProfile.mutate(u.id)}
                                  disabled={unpublishProfile.isPending}
                                  className="text-destructive border-destructive hover:bg-destructive/10"
                                >
                                  {unpublishProfile.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                  ) : (
                                    <X className="h-4 w-4 mr-1" />
                                  )}
                                  Unpublish
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setProfileToApprove({ id: u.id, full_name: u.full_name || '', role: u.role || 'entrepreneur' });
                                    setApprovalDialogOpen(true);
                                  }}
                                  disabled={publishProfile.isPending}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  {publishProfile.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                  ) : (
                                    <Check className="h-4 w-4 mr-1" />
                                  )}
                                  Publish
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedProfileForEmail({ id: u.id, email: u.email, full_name: u.full_name });
                                  setEmailDialogOpen(true);
                                }}
                              >
                                <Mail className="h-4 w-4 mr-1" />
                                Email
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setResetingUserId(u.id);
                                  resetPassword.mutate(u.email);
                                }}
                                disabled={resetPassword.isPending && resetingUserId === u.id}
                              >
                                {resetPassword.isPending && resetingUserId === u.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                ) : (
                                  <KeyRound className="h-4 w-4 mr-1" />
                                )}
                                Reset Password
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  setSetPasswordUser({ id: u.id, email: u.email, full_name: u.full_name })
                                }
                              >
                                <KeyRound className="h-4 w-4 mr-1" />
                                Set Password
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  setSelectedProfile(u);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                              {u.role?.toLowerCase() === "entrepreneur" && pitchDeckUserIds?.has(u.id) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadPitchDeck(u.id)}
                                  disabled={downloadingDeckUserId === u.id}
                                >
                                  {downloadingDeckUserId === u.id ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4 mr-1" />
                                  )}
                                  Pitch Deck
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Onboarded Toggle Row */}
                          <div className="flex items-center gap-2 pl-[52px]">
                            <Checkbox
                              id={`onboarded-${u.id}`}
                              checked={u.is_onboarded || false}
                              onCheckedChange={(checked) => {
                                toggleOnboarded.mutate({ profileId: u.id, isOnboarded: !!checked });
                              }}
                              disabled={toggleOnboarded.isPending}
                            />
                            <Label htmlFor={`onboarded-${u.id}`} className="text-sm cursor-pointer">
                              Onboarded
                            </Label>
                          </div>
                          
                          {/* Role Management Section */}
                          <div className="flex items-center gap-2 flex-wrap pt-2 border-t">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground mr-2">App Roles:</span>
                            
                            {userRoles.length > 0 ? (
                              userRoles.map((role) => (
                                <Badge
                                  key={role}
                                  variant="secondary"
                                  className="capitalize flex items-center gap-1"
                                >
                                  {role}
                                  <button
                                    onClick={() => removeRole.mutate({ userId: u.id, role, userEmail: u.email })}
                                    className="ml-1 hover:text-destructive"
                                    disabled={removeRole.isPending}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground italic">No roles assigned</span>
                            )}
                            
                            {rolesNotAssigned.length > 0 && (
                              <div className="flex items-center gap-2 ml-2">
                                <Select
                                  value={selectedRoleToAdd[u.id] || ""}
                                  onValueChange={(value) => setSelectedRoleToAdd((prev) => ({ ...prev, [u.id]: value }))}
                                >
                                  <SelectTrigger className="w-[140px] h-8">
                                    <SelectValue placeholder="Add role..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {rolesNotAssigned.map((role) => (
                                      <SelectItem key={role} value={role} className="capitalize">
                                        {role}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const role = selectedRoleToAdd[u.id];
                                    if (role) {
                                      addRole.mutate({ userId: u.id, role, userEmail: u.email });
                                      setSelectedRoleToAdd((prev) => ({ ...prev, [u.id]: "" }));
                                    }
                                  }}
                                  disabled={!selectedRoleToAdd[u.id] || addRole.isPending}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                          {/* Learning Progress Section */}
                          {(u.progressData?.curricula?.length > 0 || u.progressData?.courses?.length > 0) && (
                            <Collapsible className="pt-2 border-t">
                              <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors w-full">
                                <GraduationCap className="h-4 w-4 text-muted-foreground" />
                                <span>Learning Progress</span>
                                <Badge variant="secondary" className="ml-auto">
                                  {u.progressData.totalModulesCompleted}/{u.progressData.totalModules} modules
                                </Badge>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="pt-3 space-y-3">
                                {/* Overall Progress */}
                                <div className="pl-6">
                                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                    <span>Overall Completion</span>
                                    <span>
                                      {u.progressData.totalModules > 0 
                                        ? Math.round((u.progressData.totalModulesCompleted / u.progressData.totalModules) * 100)
                                        : 0}%
                                    </span>
                                  </div>
                                  <Progress 
                                    value={u.progressData.totalModules > 0 
                                      ? (u.progressData.totalModulesCompleted / u.progressData.totalModules) * 100 
                                      : 0} 
                                    className="h-2"
                                  />
                                </div>

                                {/* Curricula */}
                                {u.progressData.curricula.length > 0 && (
                                  <div className="pl-6 space-y-2">
                                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                      <GraduationCap className="h-3 w-3" /> Curricula
                                    </p>
                                    {u.progressData.curricula.map((curr: any) => (
                                      <div key={curr.id} className="bg-muted/50 rounded p-2">
                                        <div className="flex items-center justify-between text-sm mb-1">
                                          <span className="font-medium truncate">{curr.title}</span>
                                          <span className="text-xs text-muted-foreground shrink-0 ml-2">
                                            {curr.progress}% · {curr.completedCourses}/{curr.totalCourses} courses
                                          </span>
                                        </div>
                                        {curr.enrolled_at && (
                                          <p className="text-xs text-muted-foreground mb-1">
                                            Enrolled: {format(new Date(curr.enrolled_at), "MMM d, yyyy")}
                                          </p>
                                        )}
                                        <Progress value={curr.progress} className="h-1.5" />
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Courses */}
                                {u.progressData.courses.length > 0 && (
                                  <div className="pl-6 space-y-2">
                                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                      <BookOpen className="h-3 w-3" /> Courses
                                    </p>
                                    {u.progressData.courses.map((course: any) => (
                                      <div key={course.id} className="bg-muted/50 rounded p-2">
                                        <div className="flex items-center justify-between text-sm mb-1">
                                          <span className="font-medium truncate">{course.title}</span>
                                          <span className="text-xs text-muted-foreground shrink-0 ml-2">
                                            {course.progress}% · {course.completedModules}/{course.totalModules} modules
                                          </span>
                                        </div>
                                        {course.enrolled_at && (
                                          <p className="text-xs text-muted-foreground mb-1">
                                            Enrolled: {format(new Date(course.enrolled_at), "MMM d, yyyy")}
                                          </p>
                                        )}
                                        <Progress value={course.progress} className="h-1.5" />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </CollapsibleContent>
                            </Collapsible>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : userSearch ? (
                  <p className="text-muted-foreground text-center py-8">No users found matching "{userSearch}"</p>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No users found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="demographics" className="mt-6">
            <DemographicsAnalytics />
          </TabsContent>

          <TabsContent value="learning" className="mt-6">
            <LearningAnalytics />
          </TabsContent>

          <TabsContent value="topic-requests" className="mt-6">
            <MentorshipTopicRequestsManager />
          </TabsContent>

          <TabsContent value="direct-mentorship" className="mt-6">
            <DirectMentorshipRequestsManager />
          </TabsContent>

          <TabsContent value="admin-tools" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Admin Tools
                </CardTitle>
                <CardDescription>
                  System administration, logging, and maintenance tools
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="activity-logs" className="w-full">
                  <TabsList className="mb-4 flex-wrap h-auto gap-1">
                    <TabsTrigger value="activity-logs">
                      <History className="h-4 w-4 mr-1" />
                      Activity Logs
                    </TabsTrigger>
                    <TabsTrigger value="error-logs">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Error Logs
                    </TabsTrigger>
                    <TabsTrigger value="data-cleanup">
                      <Database className="h-4 w-4 mr-1" />
                      Data Cleanup
                    </TabsTrigger>
                    <TabsTrigger value="security-report">
                      <Shield className="h-4 w-4 mr-1" />
                      Security Report
                    </TabsTrigger>
                    <TabsTrigger value="delete-user">
                      <UserMinus className="h-4 w-4 mr-1" />
                      Delete User
                    </TabsTrigger>
                    <TabsTrigger value="mentor-topics">
                      <BookOpen className="h-4 w-4 mr-1" />
                      Mentor Topics
                    </TabsTrigger>
                    <TabsTrigger value="taxonomy-suggestions">
                      <Lightbulb className="h-4 w-4 mr-1" />
                      Taxonomy Suggestions
                    </TabsTrigger>
                    <TabsTrigger value="reset-connections">
                      <Link2Off className="h-4 w-4 mr-1" />
                      Reset Connections
                    </TabsTrigger>
                    <TabsTrigger value="discussion-moderation">
                      <MessageSquare className="h-4 w-4 mr-1" />
                      Discussions
                    </TabsTrigger>
                    <TabsTrigger value="mentorship-diagnostics">
                      <CalendarDays className="h-4 w-4 mr-1" />
                      Mentorship ICS
                    </TabsTrigger>
                    <TabsTrigger value="zoom-debug">
                      <Settings className="h-4 w-4 mr-1" />
                      Zoom Debug
                    </TabsTrigger>
                    <TabsTrigger value="scheduling-debug">
                      <Clock className="h-4 w-4 mr-1" />
                      Scheduling Debug
                    </TabsTrigger>
                    <TabsTrigger value="email-verification">
                      <Mail className="h-4 w-4 mr-1" />
                      Email Verification
                    </TabsTrigger>
                    <TabsTrigger value="email-mismatch-audit">
                      <Mail className="h-4 w-4 mr-1" />
                      Email Mismatch Audit
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="activity-logs">
                    {logsLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : activityLogs && activityLogs.length > 0 ? (
                      <div className="space-y-3">
                        {activityLogs.map((log) => {
                          const adminUser = allUsers?.find(u => u.id === log.admin_id);
                          return (
                            <div key={log.id} className="p-3 rounded-lg border bg-card flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={getStorageUrl("avatars", adminUser?.avatar_url) || undefined} />
                                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                    {adminUser?.full_name?.charAt(0) || "A"}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm">{adminUser?.full_name || "Admin"}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {log.action_type.replace(/_/g, " ")}
                                    </Badge>
                                  </div>
                                  {log.target_user_email && (
                                    <p className="text-sm text-muted-foreground mt-0.5">
                                      Target: {log.target_user_email}
                                    </p>
                                  )}
                                  {log.details && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {typeof log.details === "object" 
                                        ? Object.entries(log.details as Record<string, unknown>)
                                            .filter(([key]) => key !== "user_ids" && key !== "emails" && key !== "previous_values")
                                            .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
                                            .join(", ")
                                        : String(log.details)}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {canUndoAction(log) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleUndo(log)}
                                    disabled={
                                      undoAddRole.isPending ||
                                      undoRemoveRole.isPending ||
                                      undoEditProfile.isPending ||
                                      undoBulkAddAdminRole.isPending
                                    }
                                    className="h-7 px-2"
                                  >
                                    <Undo2 className="h-3.5 w-3.5 mr-1" />
                                    Undo
                                  </Button>
                                )}
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {format(new Date(log.created_at), "MMM d, yyyy h:mm a")}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12">
                        <History className="h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No activity logs yet</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="error-logs">
                    <ErrorLogsSection />
                  </TabsContent>

                  <TabsContent value="data-cleanup">
                    <OrphanCleanupManager />
                  </TabsContent>

                  <TabsContent value="security-report">
                    <SecurityReportManager />
                  </TabsContent>

                  <TabsContent value="delete-user">
                    <UserDeletionManager />
                  </TabsContent>

                  <TabsContent value="mentor-topics">
                    <MentorTopicsManager />
                  </TabsContent>

                  <TabsContent value="taxonomy-suggestions">
                    <TaxonomySuggestionsManager />
                  </TabsContent>

                  <TabsContent value="reset-connections">
                    <ConnectionResetManager />
                  </TabsContent>

                  <TabsContent value="discussion-moderation">
                    <DiscussionModerationManager />
                  </TabsContent>

                  <TabsContent value="mentorship-diagnostics">
                    <MentorshipMeetingsDiagnostics />
                  </TabsContent>

                  <TabsContent value="zoom-debug">
                    <ZoomDebugLogs />
                  </TabsContent>

                  <TabsContent value="scheduling-debug">
                    <SchedulingDebugManager />
                  </TabsContent>

                  <TabsContent value="email-verification">
                    <UserEmailVerificationManager />
                  </TabsContent>

                  <TabsContent value="email-mismatch-audit">
                    <EmailMismatchAudit />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="community-connections" className="mt-6">
            <ConnectionsManager />
          </TabsContent>

          <TabsContent value="email-notifications" className="mt-6">
            <EmailNotificationsManager />
          </TabsContent>

          <TabsContent value="suggested-connections" className="mt-6">
            <AdminSuggestedConnections />
          </TabsContent>

          <TabsContent value="unenrollments" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserMinus className="h-5 w-5" />
                  Course Unenrollments
                </CardTitle>
                <CardDescription>
                  Users who have unenrolled from courses
                </CardDescription>
              </CardHeader>
              <CardContent>
                {unenrollmentsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : unenrollments && unenrollments.length > 0 ? (
                  <div className="space-y-3">
                    {unenrollments.map((item: any) => (
                      <div
                        key={item.id}
                        className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={getStorageUrl("avatars", item.user?.avatar_url) || undefined} />
                            <AvatarFallback>{item.user?.full_name?.charAt(0) || "?"}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{item.user?.full_name || "Unknown User"}</p>
                            <p className="text-sm text-muted-foreground">
                              Unenrolled from: <strong>{item.course?.title || "Unknown Course"}</strong>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col md:items-end gap-1">
                            <Badge variant="outline" className="w-fit">
                              {item.progress_at_unenroll}% progress
                            </Badge>
                            <div className="text-xs text-muted-foreground">
                              <p>Unenrolled: {format(new Date(item.unenrolled_at), "MMM d, yyyy 'at' h:mm a")}</p>
                              {item.original_enrolled_at && (
                                <p>Originally enrolled: {format(new Date(item.original_enrolled_at), "MMM d, yyyy")}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMessageUser(item.user_id, item.user?.full_name)}
                            disabled={initiateConnection.isPending}
                          >
                            {initiateConnection.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MessageSquare className="h-4 w-4 mr-1" />
                            )}
                            Message
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <UserMinus className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No unenrollments recorded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events" className="mt-6">
            <EventManager />
          </TabsContent>

          <TabsContent value="calls" className="mt-6">
            <UpcomingCallsManager />
          </TabsContent>

          <TabsContent value="satisfaction" className="mt-6">
            <SatisfactionSurveyManager />
          </TabsContent>

          <TabsContent value="surveys" className="mt-6">
            <PlatformSurveyManager />
          </TabsContent>

          <TabsContent value="communications" className="mt-6">
            <Tabs defaultValue="announcements">
              <TabsList>
                <TabsTrigger value="announcements">
                  <Megaphone className="h-4 w-4 mr-1" />
                  Announcements
                </TabsTrigger>
                <TabsTrigger value="bulk-email">
                  <Mail className="h-4 w-4 mr-1" />
                  Bulk Email
                </TabsTrigger>
                <TabsTrigger value="email-templates">
                  <Mail className="h-4 w-4 mr-1" />
                  Email Templates
                </TabsTrigger>
                <TabsTrigger value="email-jobs">
                  <History className="h-4 w-4 mr-1" />
                  Email Jobs
                </TabsTrigger>
                <TabsTrigger value="help-center">
                  <HelpCircle className="h-4 w-4 mr-1" />
                  Help Center
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="announcements" className="mt-4">
                <CommunityAnnouncementsManager />
              </TabsContent>

              <TabsContent value="bulk-email" className="mt-4">
                <BulkEmailManager />
              </TabsContent>
              
              <TabsContent value="email-templates" className="mt-4">
                <div className="space-y-6">
                  <EmailTemplateManager />
                  <WelcomeEmailTemplatesManager />
                </div>
              </TabsContent>

              <TabsContent value="email-jobs" className="mt-4">
                <WelcomeEmailJobsHistory />
              </TabsContent>
              
              <TabsContent value="help-center" className="mt-4">
                <HelpCenterManager />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="ask-nedra" className="mt-6">
            <AskNedraInbox initialThreadId={activeTab === "ask-nedra" ? urlThreadId : null} />
          </TabsContent>

          <TabsContent value="recent-registrations" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Recent Registrations</CardTitle>
                <CardDescription>Users who registered in the last 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                {recentRegistrationsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : recentRegistrations && recentRegistrations.length > 0 ? (
                  <div className="space-y-3">
                    {recentRegistrations.map((profile: any) => (
                      <div
                        key={profile.id}
                        className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        {/* User info row - always horizontal */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Avatar className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0">
                            <AvatarImage src={getStorageUrl("avatars", profile.avatar_url) || undefined} />
                            <AvatarFallback className="bg-primary text-primary-foreground text-sm sm:text-base">
                              {profile.full_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate text-sm sm:text-base">{profile.full_name}</p>
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">{profile.email}</p>
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1">
                              <Badge variant="outline" className="capitalize text-xs">
                                {profile.role}
                              </Badge>
                              {profile.is_published ? (
                                <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                                  Published
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                                  Pending
                                </Badge>
                              )}
                              {profile.is_onboarded && (
                                <Badge variant="secondary" className="text-xs">
                                  Onboarded
                                </Badge>
                              )}
                            </div>
                          </div>
                          {/* Date - visible on mobile inline, hidden on desktop */}
                          <div className="text-right sm:hidden flex-shrink-0">
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(profile.created_at), "MMM d")}
                            </p>
                          </div>
                        </div>
                        
                        {/* Date - hidden on mobile, visible on desktop */}
                        <div className="hidden sm:block text-right flex-shrink-0">
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(profile.created_at), "MMM d, yyyy")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(profile.created_at), "h:mm a")}
                          </p>
                        </div>
                        
                        {/* Action buttons - stack on mobile, row on desktop */}
                        <div className="flex items-center gap-2 sm:gap-2 sm:flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 sm:flex-none h-8 px-2 sm:px-3"
                            onClick={() => {
                              setViewingUserDetail({
                                ...profile,
                                skills: [],
                                industries: [],
                              });
                              setShowUserDetailDialog(true);
                            }}
                          >
                            <Eye className="h-4 w-4 sm:mr-1" />
                            <span className="hidden sm:inline">View</span>
                          </Button>
                          {!profile.is_published && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setProfileToApprove({ id: profile.id, full_name: profile.full_name || '', role: profile.role || 'entrepreneur' });
                                setApprovalDialogOpen(true);
                              }}
                              disabled={publishProfile.isPending}
                              className="flex-1 sm:flex-none h-8 px-2 sm:px-3 bg-green-600 hover:bg-green-700 text-white"
                            >
                              <Check className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">Publish</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <UserPlus className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No registrations in the last 30 days</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="email-testing" className="mt-6">
            <EmailTestingManager />
          </TabsContent>


          <TabsContent value="gallery" className="mt-6">
            <GalleryManager />
          </TabsContent>

          <TabsContent value="settings" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Homepage Hero Banner
                </CardTitle>
                <CardDescription>
                  Set the featured image displayed in the hero section of the homepage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-w-md">
                  <ImagePicker
                    value={heroBannerSetting?.value}
                    onChange={(url) => updateHeroBanner.mutate(url)}
                    label="Hero Banner Image"
                  />
                </div>
                {heroBannerSetting?.value && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Current Preview</Label>
                    <div className="relative aspect-[21/9] max-w-2xl overflow-hidden rounded-lg border bg-primary">
                      <div 
                        className="absolute inset-0 opacity-30"
                        style={{
                          backgroundImage: `url(${heroBannerSetting.value})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <p className="text-primary-foreground font-semibold text-lg">Hero Banner Preview</p>
                      </div>
                    </div>
                    {heroBannerSetting?.updated_at && (
                      <p className="text-xs text-muted-foreground">
                        Last updated: {format(new Date(heroBannerSetting.updated_at), "PPp")}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Connection Request Limits
                </CardTitle>
                <CardDescription>
                  Control whether entrepreneurs are limited to 3 connection requests per week
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="connection-limit-toggle" className="text-base">
                      Enable Weekly Request Limit
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      When enabled, entrepreneurs can only send 3 mentor connection requests per week. 
                      Same-role connections (mentor-to-mentor, entrepreneur-to-entrepreneur) are never limited.
                    </p>
                  </div>
                  <Switch
                    id="connection-limit-toggle"
                    checked={connectionLimitEnabled}
                    onCheckedChange={(checked) => toggleConnectionLimit.mutate(checked)}
                    disabled={toggleConnectionLimit.isPending}
                  />
                </div>
                {connectionLimitSetting?.updated_at && (
                  <p className="text-xs text-muted-foreground">
                    Last updated: {format(new Date(connectionLimitSetting.updated_at), "PPp")}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  Dashboard Leaderboards
                </CardTitle>
                <CardDescription>
                  Display leaderboards on all user dashboards
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Learning Leaderboard Toggle */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="leaderboard-toggle" className="text-base">
                        Most Active Learners Leaderboard
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Shows top 10 users by learning activity (modules, assignments, quizzes, discussions).
                      </p>
                    </div>
                    <Switch
                      id="leaderboard-toggle"
                      checked={leaderboardEnabled}
                      onCheckedChange={(checked) => toggleLeaderboard.mutate(checked)}
                      disabled={toggleLeaderboard.isPending}
                    />
                  </div>
                  {leaderboardSetting?.updated_at && (
                    <p className="text-xs text-muted-foreground">
                      Last updated: {format(new Date(leaderboardSetting.updated_at), "PPp")}
                    </p>
                  )}
                </div>

                <div className="border-t pt-4" />

                {/* Connection Leaderboard Toggle */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="connection-leaderboard-toggle" className="text-base">
                        Top Connectors Leaderboard
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Shows top 10 users by number of connections (accepted and completed).
                      </p>
                    </div>
                    <Switch
                      id="connection-leaderboard-toggle"
                      checked={connectionLeaderboardEnabled}
                      onCheckedChange={(checked) => toggleConnectionLeaderboard.mutate(checked)}
                      disabled={toggleConnectionLeaderboard.isPending}
                    />
                  </div>
                  {connectionLeaderboardSetting?.updated_at && (
                    <p className="text-xs text-muted-foreground">
                      Last updated: {format(new Date(connectionLeaderboardSetting.updated_at), "PPp")}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Hide Not Started Assignments Toggle */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Assignment Display Settings
                </CardTitle>
                <CardDescription>
                  Control what is displayed in the My Assignments section on the Learning page
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="hide-not-started-toggle" className="text-base">
                        Hide "Not Started" & "Completed" Assignments
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Hides the "Not Started" and "Completed" sections from the My Assignments view on the Learning page.
                      </p>
                    </div>
                    <Switch
                      id="hide-not-started-toggle"
                      checked={hideNotStartedEnabled}
                      onCheckedChange={(checked) => toggleHideNotStarted.mutate(checked)}
                      disabled={toggleHideNotStarted.isPending}
                    />
                  </div>
                  {hideNotStartedSetting?.updated_at && (
                    <p className="text-xs text-muted-foreground">
                      Last updated: {format(new Date(hideNotStartedSetting.updated_at), "PPp")}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <CurriculumReorderManager />

            <CurriculumVisibilityManager />

            <Card>
              <CardHeader>
                <CardTitle>Homepage Content Blocks</CardTitle>
                <CardDescription>
                  Add and manage content elements displayed on the homepage (images, videos, text, headings)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <HomepageContentManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bulk-import" className="mt-6">
            <BulkImport />
          </TabsContent>
        </Tabs>
      </div>

      {/* Profile Review Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Profile</DialogTitle>
            <DialogDescription>Review this profile before publishing</DialogDescription>
          </DialogHeader>
          {selectedProfile && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={getStorageUrl("avatars", selectedProfile.avatar_url) || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                    {selectedProfile.full_name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-lg">{selectedProfile.full_name}</p>
                  <p className="text-muted-foreground">{selectedProfile.email}</p>
                  <Badge className="mt-1 capitalize">{selectedProfile.role}</Badge>
                </div>
              </div>

              {selectedProfile.bio && (
                <div>
                  <Label className="text-muted-foreground">Bio</Label>
                  <p className="mt-1">{selectedProfile.bio}</p>
                </div>
              )}

              {selectedProfile.linkedin_url && (
                <div>
                  <Label className="text-muted-foreground">LinkedIn</Label>
                  <a
                    href={selectedProfile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline block mt-1"
                  >
                    {selectedProfile.linkedin_url}
                  </a>
                </div>
              )}

              <div className="flex items-center space-x-2 pt-4">
                <Checkbox id="verified" defaultChecked />
                <Label htmlFor="verified">I have personally verified this profile</Label>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowProfileDialog(false)}>
              Cancel
            </Button>
            {selectedProfile?.is_published ? (
              <Button
                onClick={() => selectedProfile && unpublishProfile.mutate(selectedProfile.id)}
                disabled={unpublishProfile.isPending}
                variant="destructive"
              >
                {unpublishProfile.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <X className="h-4 w-4 mr-1" />
                Unpublish Profile
              </Button>
            ) : (
              <Button
                onClick={() => {
                  if (selectedProfile) {
                    setProfileToApprove({ id: selectedProfile.id, full_name: selectedProfile.full_name || '', role: selectedProfile.role || 'entrepreneur' });
                    setApprovalDialogOpen(true);
                  }
                }}
                disabled={publishProfile.isPending}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {publishProfile.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Check className="h-4 w-4 mr-1" />
                Publish Profile
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription>Update user profile information</DialogDescription>
          </DialogHeader>
          {selectedProfile && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Full Name</Label>
                  <Input
                    id="edit-name"
                    value={editFormData.full_name}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, full_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-role">User Type</Label>
                  <Select
                    value={editFormData.role}
                    onValueChange={(value: "entrepreneur" | "mentor" | "admin") =>
                      setEditFormData((prev) => ({ ...prev, role: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
                      <SelectItem value="mentor">Mentor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-linkedin">LinkedIn URL</Label>
                  <Input
                    id="edit-linkedin"
                    value={editFormData.linkedin_url}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, linkedin_url: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <div className="flex gap-2">
                  <PhoneInput
                    countryCode={editFormData.phone_country_code}
                    phoneNumber={editFormData.phone_number}
                    onCountryCodeChange={(code) => setEditFormData((prev) => ({ ...prev, phone_country_code: code }))}
                    onPhoneNumberChange={(num) => setEditFormData((prev) => ({ ...prev, phone_number: num }))}
                  />
                  <Select
                    value={editFormData.phone_type}
                    onValueChange={(value) => setEditFormData((prev) => ({ ...prev, phone_type: value }))}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-background">
                      {PHONE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-country">Country</Label>
                  <Select
                    value={editFormData.country}
                    onValueChange={(value) => setEditFormData((prev) => ({ ...prev, country: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-parish">Parish</Label>
                  <Select
                    value={editFormData.parish}
                    onValueChange={(value) => setEditFormData((prev) => ({ ...prev, parish: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parish" />
                    </SelectTrigger>
                    <SelectContent>
                      {JAMAICAN_PARISHES.map((parish) => (
                        <SelectItem key={parish.value} value={parish.value}>
                          {parish.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-bio">Bio</Label>
                <Textarea
                  id="edit-bio"
                  value={editFormData.bio}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, bio: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium text-muted-foreground mb-3">Demographic Information</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-gender">Gender</Label>
                    <Select
                      value={editFormData.gender}
                      onValueChange={(value) => setEditFormData((prev) => ({ ...prev, gender: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDER_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-age-range">Age Range</Label>
                    <Select
                      value={editFormData.age_range}
                      onValueChange={(value) => setEditFormData((prev) => ({ ...prev, age_range: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select age range" />
                      </SelectTrigger>
                      <SelectContent>
                        {AGE_RANGE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-education">Education Level</Label>
                    <Select
                      value={editFormData.education_level}
                      onValueChange={(value) => setEditFormData((prev) => ({ ...prev, education_level: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select education level" />
                      </SelectTrigger>
                      <SelectContent>
                        {EDUCATION_LEVEL_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-device">Has Device</Label>
                    <Select
                      value={editFormData.has_device}
                      onValueChange={(value) => setEditFormData((prev) => ({ ...prev, has_device: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                      <SelectContent>
                        {HAS_DEVICE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <Label htmlFor="edit-referred">Referred By</Label>
                  <Select
                    value={editFormData.referred_by}
                    onValueChange={(value) => setEditFormData((prev) => ({ ...prev, referred_by: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select referral source" />
                    </SelectTrigger>
                    <SelectContent>
                      {REFERRED_BY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedProfile) {
                  const { countryCode: prevCode, phoneNumber: prevNum } = parsePhoneNumber(selectedProfile.phone || "");
                  updateProfile.mutate({
                    profileId: selectedProfile.id,
                    updates: editFormData,
                    userEmail: selectedProfile.email,
                    originalEmail: selectedProfile.email,
                    previousValues: {
                      full_name: selectedProfile.full_name,
                      email: selectedProfile.email,
                      bio: selectedProfile.bio || "",
                      role: selectedProfile.role,
                      phone_country_code: prevCode,
                      phone_number: prevNum,
                      phone_type: selectedProfile.phone_type || "",
                      parish: selectedProfile.parish || "",
                      country: (selectedProfile as any).country || "",
                      linkedin_url: selectedProfile.linkedin_url || "",
                      gender: selectedProfile.gender || "",
                      age_range: selectedProfile.age_range || "",
                      education_level: selectedProfile.education_level || "",
                      has_device: selectedProfile.has_device || "",
                      referred_by: selectedProfile.referred_by || "",
                    },
                  });
                }
              }}
              disabled={updateProfile.isPending}
            >
              {updateProfile.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedProfile?.full_name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedProfile && deleteUser.mutate({ profileId: selectedProfile.id, userEmail: selectedProfile.email })}
              disabled={deleteUser.isPending}
            >
              {deleteUser.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Trash2 className="h-4 w-4 mr-1" />
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Multiple Users</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUsers.size} users? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => bulkDeleteUsers.mutate({ userIds: Array.from(selectedUsers), count: selectedUsers.size })}
              disabled={bulkDeleteUsers.isPending}
            >
              {bulkDeleteUsers.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Trash2 className="h-4 w-4 mr-1" />
              Delete {selectedUsers.size} Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <AdminSetPasswordDialog
        open={!!setPasswordUser}
        onOpenChange={(o) => { if (!o) setSetPasswordUser(null); }}
        userId={setPasswordUser?.id ?? null}
        userEmail={setPasswordUser?.email ?? null}
        userName={setPasswordUser?.full_name ?? null}
      />

      <ProfileDetailDialog
        member={viewingUserDetail}
        open={showUserDetailDialog}
        onOpenChange={setShowUserDetailDialog}
        showAllFields={true}
        showContactInfo={true}
        onEdit={() => {
          setShowUserDetailDialog(false);
          if (viewingUserDetail) {
            setSelectedProfile(viewingUserDetail);
            const { countryCode, phoneNumber } = parsePhoneNumber(viewingUserDetail.phone || "");
            setEditFormData({
              full_name: viewingUserDetail.full_name || "",
              email: viewingUserDetail.email || "",
              bio: viewingUserDetail.bio || "",
              role: viewingUserDetail.role as "entrepreneur" | "mentor" | "admin",
              phone_country_code: countryCode,
              phone_number: phoneNumber,
              phone_type: viewingUserDetail.phone_type || "",
              parish: viewingUserDetail.parish || "",
              country: (viewingUserDetail as any).country || "",
              linkedin_url: viewingUserDetail.linkedin_url || "",
              gender: viewingUserDetail.gender || "",
              age_range: viewingUserDetail.age_range || "",
              education_level: viewingUserDetail.education_level || "",
              has_device: viewingUserDetail.has_device || "",
              referred_by: viewingUserDetail.referred_by || "",
            });
            setShowEditDialog(true);
          }
        }}
      />


      {/* Profile Approval Confirmation Dialog */}
      <AlertDialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve This Profile?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>By clicking "I Approve", you confirm that:</p>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>You have personally reviewed this profile</li>
                  <li>The information provided meets EAB Connect standards</li>
                  <li>This user is ready to join the community</li>
                </ul>
                {profileToApprove && (
                  <p className="font-medium text-foreground pt-2">
                    User: {profileToApprove.full_name} ({profileToApprove.role})
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (profileToApprove) {
                  publishProfile.mutate(profileToApprove.id);
                  setShowProfileDialog(false);
                }
                setApprovalDialogOpen(false);
                setProfileToApprove(null);
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={publishProfile.isPending}
            >
              {publishProfile.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              I Have Personally Approved This Profile
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Profile Approval Confirmation Dialog */}
      <AlertDialog open={bulkApprovalDialogOpen} onOpenChange={setBulkApprovalDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve {profilesToApprove.length} Profile{profilesToApprove.length !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>By clicking "I Approve All", you confirm that:</p>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>You have personally reviewed all selected profiles</li>
                  <li>The information provided meets EAB Connect standards</li>
                  <li>These users are ready to join the community</li>
                </ul>
                <p className="font-medium text-foreground pt-2">
                  {profilesToApprove.length} profile{profilesToApprove.length !== 1 ? 's' : ''} will be published
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                bulkPublishProfiles.mutate(profilesToApprove);
                setBulkApprovalDialogOpen(false);
                setProfilesToApprove([]);
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={bulkPublishProfiles.isPending}
            >
              {bulkPublishProfiles.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              I Have Personally Approved All Selected
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Profile Email Dialog */}
      {selectedProfileForEmail && (
        <SendProfileNotificationDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          profileId={selectedProfileForEmail.id}
          recipientEmail={selectedProfileForEmail.email || ""}
          recipientName={selectedProfileForEmail.full_name || "User"}
        />
      )}
      </>
      )}
    </DashboardLayout>
  );
}
